import { useState } from 'react';
import { SendIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { liveConversations, type Conversation } from '../scenarios';

interface DemoInputBoxProps {
  disabled: boolean;
  onPick: (conversation: Conversation) => void;
}

function normalize(text: string): string[] {
  return text
    .toLowerCase()
    .replace(/[^\p{L}\p{N}\s]/gu, ' ')
    .split(/\s+/)
    .filter((w) => w.length > 2);
}

/** Word-overlap score of typed text vs each live conversation's label + first question. */
export function matchConversation(text: string): Conversation | null {
  const words = normalize(text);
  if (words.length === 0) return null;
  let best: Conversation | null = null;
  let bestScore = 0;
  for (const conversation of liveConversations) {
    const firstQuestion =
      conversation.steps[0].kind === 'user' ? conversation.steps[0].content : '';
    const target = new Set(normalize(`${conversation.label} ${conversation.title} ${firstQuestion}`));
    const score = words.filter((w) => target.has(w)).length;
    if (score > bestScore) {
      bestScore = score;
      best = conversation;
    }
  }
  return best;
}

export const DemoInputBox = ({ disabled, onPick }: DemoInputBoxProps): JSX.Element => {
  const [value, setValue] = useState('');
  const [showHint, setShowHint] = useState(false);

  const submit = () => {
    if (disabled || !value.trim()) return;
    const match = matchConversation(value);
    if (match) {
      setValue('');
      setShowHint(false);
      onPick(match);
    } else {
      setShowHint(true);
    }
  };

  return (
    <div className="space-y-1">
      <div className="flex items-center gap-2">
        <Input
          value={value}
          disabled={disabled}
          placeholder="Попитайте Company Brain…"
          className="h-11 rounded-xl"
          onChange={(e) => {
            setValue(e.target.value);
            setShowHint(false);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') submit();
          }}
        />
        <Button
          size="icon"
          variant="blue"
          className="h-11 w-11 rounded-xl"
          disabled={disabled || !value.trim()}
          onClick={submit}
        >
          <SendIcon className="h-4 w-4" />
        </Button>
      </div>
      {showHint && (
        <p className="px-1 text-xs text-neutral-400">
          Не открих подходящ отговор — изберете въпрос от предложените.
        </p>
      )}
    </div>
  );
};
